import React, { createRef, useMemo, useRef } from 'react';
import { GetServerSideProps, NextPageWithLayout } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { startsWithLetter } from '@helpers/utils/general';
import clsx from 'clsx';
import extractFromContext from '@helpers/utils/extract-from-context';
import Link from '@components/atoms/link/link';
import DefaultLayout from '@layouts/default-layout';
import { isRequest404Error, isRequestError } from '@helpers/errors';
import logger from '@helpers/utils/logger/client';
import { getBrands } from '@api/brands';
import { useTranslation } from 'next-i18next';
import { useBucket } from '@context/bucket.context';
import { FAST_FASHION_EDITO_URL } from '@helpers/utils/catalog/fast-fashion-ban-utils';
import Head from '@components/atoms/head/head';
import { usePreferences } from '@context/preferences.context';
import { defaultPageMetasForBrandPage } from '@constants/metas/default';
import CatalogHeader from '@components/catalog/catalog-header/catalog-header';
import { getAppInitialProps } from '@helpers/initial-props';
import { BrandListWithUrl } from '@interfaces/models/catalog/domain/BrandListWithUrl';
import styles from './index.module.scss';

export type BrandsPageProps = {
  brands: BrandListWithUrl[];
};

const Brands: NextPageWithLayout<BrandsPageProps> = (props) => {
  const { brands } = props;
  const { t } = useTranslation('brands');
  const { isFeatureEnabled } = useBucket();
  const { language } = usePreferences();
  const isFfBannerBrandsAzEnabled = isFeatureEnabled((f) => f.enableFfBannerBrandsAzWeb);

  // Create firstLetters and chunkedBrands in a single useMemo
  const { firstLetters, chunkedBrands } = useMemo(() => {
    // Get unique first letters (excluding non-letters which will go to '#' category)
    const letters = [
      '#',
      ...Array.from(
        new Set(
          brands.map((brand) => brand.normalizedName.charAt(0)).filter((firstLetter) => startsWithLetter(firstLetter)),
        ),
      ).sort((a, b) => a.localeCompare(b)),
    ];

    // Create chunked brands based on first letters
    const chunks = [
      brands.filter((brand) => !startsWithLetter(brand.normalizedName)),
      ...letters.slice(1).map((firstLetter) => brands.filter((brand) => brand.normalizedName.startsWith(firstLetter))),
    ];

    return {
      firstLetters: letters,
      chunkedBrands: chunks,
    };
  }, [brands]);

  // React ref to store array of refs
  const scrollRefs = useRef<React.RefObject<HTMLLIElement>[]>([]);
  // Populate scrollable refs, only create them once
  // if the selectedElements array length is expected to change there is a workaround
  scrollRefs.current = firstLetters.map((_, i) => scrollRefs.current[i] ?? createRef());

  // Curried handler to take index and return click handler
  const scrollSmoothHandler = (index: number) => () => {
    scrollRefs.current[index].current.scrollIntoView({
      block: 'start',
      behavior: 'smooth',
    });
  };

  // static breadcrumbs for brand page
  const breadCrumbsBrandsPage = [
    {
      localizedName: t('BRANDS.BREADCRUMBS_HOME'),
      url: {
        path: '/',
      },
    },
    {
      localizedName: t('BRANDS.BREADCRUMBS_DESIGNERS'),
      url: {
        path: '/brands/',
      },
    },
  ];

  return (
    <>
      <Head metadata={defaultPageMetasForBrandPage[language]} />
      {isFfBannerBrandsAzEnabled && (
        <div className={styles.brands__ffBanBanner}>
          {t('BRANDS.FF_BAN_BANNER.TITLE')}
          <Link
            href={FAST_FASHION_EDITO_URL}
            target={'__blank'}
          >
            {t('BRANDS.FF_BAN_BANNER.CTA')}
          </Link>
        </div>
      )}
      <main className={clsx('innerContainer', styles.brands)}>
        <CatalogHeader
          title={defaultPageMetasForBrandPage[language].headingTitle || ''}
          breadcrumbs={breadCrumbsBrandsPage}
          customClassName={styles.brands__header}
        />
        <div className={styles.brands__alphabeticNavigation}>
          {firstLetters.map((letter: string, index: number) => (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/interactive-supports-focus
            <a
              role="link"
              className={styles['brands__alphabeticNavigation--link']}
              onClick={scrollSmoothHandler(index)}
              key={letter}
            >
              {letter.toUpperCase()}
            </a>
          ))}
        </div>

        <ul>
          {chunkedBrands.map((brandListByFirstLetter, index) => (
            <li
              // Since our list never re-renders, we don't need to have an actual key
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              className={styles.brand}
              ref={scrollRefs.current[index]}
            >
              <p className={clsx(styles.brand__firstLetter)}>
                {index === 0 ? '#' : brandListByFirstLetter[0].normalizedName.charAt(0)}
              </p>
              <ul className={clsx(styles.brand__list)}>
                {brandListByFirstLetter.map((brand: BrandListWithUrl) => (
                  <li
                    key={brand.id}
                    className={styles['brand__list--link']}
                  >
                    <Link
                      href={brand.url.path}
                      rel="internal-marques"
                    >
                      {brand.normalizedName}
                    </Link>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </main>
    </>
  );
};

Brands.Layout = [DefaultLayout];
Brands.PageType = 'BrandsPage';

export const getServerSideProps: GetServerSideProps = async (context) => {
  context.res.setHeader('x-vc-page', 'staticPage');

  const { locale } = extractFromContext(context);

  try {
    const [initialProps, translations, brands] = await Promise.all([
      getAppInitialProps(context),
      serverSideTranslations(locale, ['default-layout', 'vc-authentication', 'a11y', 'brands', 'common']),
      getBrands(),
    ]);

    return {
      props: {
        ...initialProps,
        ...translations,
        brands,
      },
    };
  } catch (error) {
    if (isRequestError(error)) {
      if (!isRequest404Error(error)) {
        logger.error(error, 'There was an api request error on the Brands Page - getServerSideProps - index.page.tsx');
      }
      return {
        notFound: true,
      };
    }
    logger.error(error, 'The server failed to render the Brands Page - getServerSideProps - index.page.tsx');
    throw error;
  }
};

export default Brands;
